import {
  getCallsOverview,
  listTopCallers,
  listCallsByUser,
  listServiceStats,
  listDailyStats,
  listAvailableYears,
  getCallerBreakdown,
  listStatisticsUsers
} from '@/api/api.js';
import { clampDateRangeToYear } from '@/api/statisticsParams.js';

const toInt = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  const n = parseInt(value, 10);

  return Number.isNaN(n) ? 0 : n;
};

const normalizeBreakdown = (items) =>
  (items || []).map(item => ({
    ...item,
    call_count: toInt(item.call_count)
  }));

const normalizeOverview = (data) => {
  if (!data) {
    return null;
  }

  return {
    ...data,
    year: toInt(data.year),
    total_calls: toInt(data.total_calls),
    unique_users: toInt(data.unique_users),
    calls_today: toInt(data.calls_today),
    calls_with_geo: toInt(data.calls_with_geo),
    unique_stations: toInt(data.unique_stations),
    unique_workspaces: toInt(data.unique_workspaces),
    top_workspaces: normalizeBreakdown(data.top_workspaces),
    by_service: normalizeBreakdown(data.by_service)
  };
};

const normalizeTopCallers = (rows) =>
  (rows || []).map(row => ({
    ...row,
    total_calls: toInt(row.total_calls)
  }));

const normalizeServiceStats = (rows) =>
  (rows || []).map(row => ({
    ...row,
    call_count: toInt(row.call_count),
    workspace_count: toInt(row.workspace_count),
    layer_count: toInt(row.layer_count),
    time_duration_count: toInt(row.time_duration_count),
    timezone_count: toInt(row.timezone_count),
    output_fmt_count: toInt(row.output_fmt_count),
    workspaces: normalizeBreakdown(row.workspaces),
    layers: normalizeBreakdown(row.layers),
    time_durations: normalizeBreakdown(row.time_durations),
    timezones: normalizeBreakdown(row.timezones),
    output_formats: normalizeBreakdown(row.output_formats)
  }));

const normalizeDailyStats = (rows) =>
  (rows || []).map(row => ({
    ...row,
    call_count: toInt(row.call_count)
  }));

const normalizeCallerBreakdown = (data) => {
  if (!data) {
    return null;
  }

  return {
    ...data,
    total_calls: toInt(data.total_calls),
    services: normalizeBreakdown(data.services),
    workspaces: normalizeBreakdown(data.workspaces),
    layers: normalizeBreakdown(data.layers),
    time_durations: normalizeBreakdown(data.time_durations)
  };
};

const buildFallbackYears = () => {
  const current = new Date().getFullYear();
  const years = [];

  for (let i = 0;i < 8;i += 1) {
    years.push(current - i);
  }

  return years;
};

export const namespaced = true;

export const state = {
  availableYears: [],
  selectedYear: new Date().getFullYear(),
  dateFrom: null,
  dateTo: null,
  overview: null,
  topCallers: [],
  statisticsUsers: [],
  callerBreakdown: null,
  callsByUser: [],
  serviceStats: [],
  dailyStats: [],
  trendUsername: null,
  loadingOverview: false,
  loadingTopCallers: false,
  loadingStatisticsUsers: false,
  loadingCallerBreakdown: false,
  loadingCallsByUser: false,
  loadingServiceStats: false,
  loadingDailyStats: false,
  serviceStatsLoaded: false,
  errors: {
    overview: false,
    topCallers: false,
    serviceStats: false,
    dailyStats: false
  }
};

export const mutations = {
  SET_AVAILABLE_YEARS(state, years) {
    state.availableYears = years;
  },
  SET_SELECTED_YEAR(state, year) {
    state.selectedYear = year;
  },
  SET_DATE_FROM(state, value) {
    state.dateFrom = value || null;
  },
  SET_DATE_TO(state, value) {
    state.dateTo = value || null;
  },
  SET_TREND_USERNAME(state, value) {
    state.trendUsername = value || null;
  },
  SET_OVERVIEW(state, data) {
    state.overview = data;
  },
  SET_TOP_CALLERS(state, data) {
    state.topCallers = data;
  },
  SET_STATISTICS_USERS(state, data) {
    state.statisticsUsers = data;
  },
  SET_CALLER_BREAKDOWN(state, data) {
    state.callerBreakdown = data;
  },
  SET_CALLS_BY_USER(state, data) {
    state.callsByUser = data;
  },
  SET_SERVICE_STATS(state, data) {
    state.serviceStats = data;
  },
  SET_DAILY_STATS(state, data) {
    state.dailyStats = data;
  },
  SET_LOADING(state, { key, value }) {
    state[key] = value;
  },
  SET_SERVICE_STATS_LOADED(state, value) {
    state.serviceStatsLoaded = value;
  },
  SET_ERROR(state, { key, value }) {
    state.errors = { ...state.errors, [key]: value };
  }
};

export const actions = {
  async storeAvailableYears({ commit, dispatch }) {
    try {
      const res = await listAvailableYears();
      const years = (res.data.listAvailableYears || [])
        .map(y => toInt(y))
        .filter(y => y > 0)
        .sort((a, b) => b - a);

      commit('SET_AVAILABLE_YEARS', years.length ? years : buildFallbackYears());
    } catch (err) {
      commit('SET_AVAILABLE_YEARS', buildFallbackYears());
      dispatch(
        'storeToastMessage',
        {
          text: 'Could not load available years; using default range',
          errObj: err,
          type: 'warning'
        },
        { root: true }
      );
    }
  },

  syncSelectedYear({ commit, state }) {
    const years = state.availableYears;

    if (!years.length) {
      return;
    }

    if (!years.includes(state.selectedYear)) {
      commit('SET_SELECTED_YEAR', years[0]);
    }
  },

  setDateRange({ commit, state }, { date_from, date_to }) {
    const clamped = clampDateRangeToYear(date_from, date_to, state.selectedYear);

    commit('SET_DATE_FROM', clamped.date_from);
    commit('SET_DATE_TO', clamped.date_to);
  },

  async initStatisticsPage({ dispatch }) {
    await dispatch('storeAvailableYears');
    await dispatch('syncSelectedYear');
    await dispatch('loadStatisticsData');
  },

  async loadStatisticsData({ commit, dispatch, state }) {
    commit('SET_SERVICE_STATS_LOADED', false);
    commit('SET_SERVICE_STATS', []);
    commit('SET_ERROR', { key: 'serviceStats', value: false });

    await Promise.all([
      dispatch('storeCallsOverview'),
      dispatch('storeTopCallers', 100),
      dispatch('storeStatisticsUsers'),
      dispatch('storeDailyStats', state.trendUsername)
    ]);
  },

  setSelectedYear({ commit }, year) {
    commit('SET_SELECTED_YEAR', toInt(year));
    commit('SET_DATE_FROM', null);
    commit('SET_DATE_TO', null);
  },

  setTrendUsername({ commit, dispatch }, username) {
    commit('SET_TREND_USERNAME', username);

    return dispatch('storeDailyStats', username);
  },

  async storeCallsOverview({ commit, dispatch, state }) {
    commit('SET_LOADING', { key: 'loadingOverview', value: true });
    commit('SET_ERROR', { key: 'overview', value: false });

    try {
      const res = await getCallsOverview(state.selectedYear, state.dateFrom, state.dateTo);
      commit('SET_OVERVIEW', normalizeOverview(res.data.getCallsOverview));
    } catch (err) {
      commit('SET_OVERVIEW', null);
      commit('SET_ERROR', { key: 'overview', value: true });
      dispatch(
        'storeToastMessage',
        { text: 'Failed to load overview', errObj: err, type: 'danger' },
        { root: true }
      );
    } finally {
      commit('SET_LOADING', { key: 'loadingOverview', value: false });
    }
  },

  async storeTopCallers({ commit, dispatch, state }, limit = 50) {
    commit('SET_LOADING', { key: 'loadingTopCallers', value: true });
    commit('SET_ERROR', { key: 'topCallers', value: false });

    try {
      const res = await listTopCallers(state.selectedYear, limit, state.dateFrom, state.dateTo);
      commit('SET_TOP_CALLERS', normalizeTopCallers(res.data.listTopCallers));
    } catch (err) {
      commit('SET_TOP_CALLERS', []);
      commit('SET_ERROR', { key: 'topCallers', value: true });
      dispatch(
        'storeToastMessage',
        { text: 'Failed to load top callers', errObj: err, type: 'danger' },
        { root: true }
      );
    } finally {
      commit('SET_LOADING', { key: 'loadingTopCallers', value: false });
    }
  },

  async storeStatisticsUsers({ commit, state }) {
    commit('SET_LOADING', { key: 'loadingStatisticsUsers', value: true });

    try {
      const res = await listStatisticsUsers(state.selectedYear, 500, state.dateFrom, state.dateTo);
      commit('SET_STATISTICS_USERS', normalizeTopCallers(res.data.listStatisticsUsers));
    } catch (err) {
      commit('SET_STATISTICS_USERS', []);
    } finally {
      commit('SET_LOADING', { key: 'loadingStatisticsUsers', value: false });
    }
  },

  async storeCallerBreakdown({ commit, dispatch, state }, username) {
    if (!username) {
      commit('SET_CALLER_BREAKDOWN', null);

      return;
    }

    commit('SET_LOADING', { key: 'loadingCallerBreakdown', value: true });

    try {
      const res = await getCallerBreakdown(
        state.selectedYear,
        username,
        state.dateFrom,
        state.dateTo
      );
      commit('SET_CALLER_BREAKDOWN', normalizeCallerBreakdown(res.data.getCallerBreakdown));
    } catch (err) {
      commit('SET_CALLER_BREAKDOWN', null);
      dispatch(
        'storeToastMessage',
        { text: 'Failed to load caller breakdown', errObj: err, type: 'danger' },
        { root: true }
      );
    } finally {
      commit('SET_LOADING', { key: 'loadingCallerBreakdown', value: false });
    }
  },

  async storeCallsByUser({ commit, dispatch, state }, { username, date_from, date_to }) {
    commit('SET_LOADING', { key: 'loadingCallsByUser', value: true });

    try {
      const { date_from: from, date_to: to } = clampDateRangeToYear(
        date_from || state.dateFrom,
        date_to || state.dateTo,
        state.selectedYear
      );
      const res = await listCallsByUser(state.selectedYear, username, from, to);
      commit('SET_CALLS_BY_USER', res.data.listCallsByUser || []);
    } catch (err) {
      commit('SET_CALLS_BY_USER', []);
      dispatch(
        'storeToastMessage',
        { text: 'Failed to load user calls', errObj: err, type: 'danger' },
        { root: true }
      );
    } finally {
      commit('SET_LOADING', { key: 'loadingCallsByUser', value: false });
    }
  },

  async storeServiceStats({ commit, dispatch, state }) {
    commit('SET_LOADING', { key: 'loadingServiceStats', value: true });
    commit('SET_ERROR', { key: 'serviceStats', value: false });

    try {
      const res = await listServiceStats(state.selectedYear, state.dateFrom, state.dateTo);
      commit('SET_SERVICE_STATS', normalizeServiceStats(res.data.listServiceStats));
      commit('SET_SERVICE_STATS_LOADED', true);
    } catch (err) {
      commit('SET_SERVICE_STATS', []);
      commit('SET_ERROR', { key: 'serviceStats', value: true });
      dispatch(
        'storeToastMessage',
        { text: 'Failed to load service stats', errObj: err, type: 'danger' },
        { root: true }
      );
    } finally {
      commit('SET_LOADING', { key: 'loadingServiceStats', value: false });
    }
  },

  async storeDailyStats({ commit, dispatch, state }, username = null) {
    commit('SET_LOADING', { key: 'loadingDailyStats', value: true });
    commit('SET_ERROR', { key: 'dailyStats', value: false });

    try {
      const user = username !== undefined ? username : state.trendUsername;
      const res = await listDailyStats(state.selectedYear, user, state.dateFrom, state.dateTo);
      commit('SET_DAILY_STATS', normalizeDailyStats(res.data.listDailyStats));
    } catch (err) {
      commit('SET_DAILY_STATS', []);
      commit('SET_ERROR', { key: 'dailyStats', value: true });
      dispatch(
        'storeToastMessage',
        { text: 'Failed to load daily stats', errObj: err, type: 'danger' },
        { root: true }
      );
    } finally {
      commit('SET_LOADING', { key: 'loadingDailyStats', value: false });
    }
  }
};

export const getters = {
  totalCallsFormatted: (state) =>
    state.overview ? state.overview.total_calls.toLocaleString('it-IT') : '—',

  serviceChartData: (state) =>
    state.serviceStats.map(s => ({ label: s.service, value: s.call_count })),

  dailyChartData: (state) =>
    state.dailyStats.map(d => ({ x: d.day, y: d.call_count })),

  userSelectItems: (state) =>
    state.statisticsUsers.map(u => ({
      text: `${u.username} (${u.email}) — ${u.total_calls.toLocaleString('it-IT')} calls`,
      value: u.username
    })),

  hasDateFilter: (state) => Boolean(state.dateFrom || state.dateTo)
};
